import React, { useState, useEffect } from 'react';
import { FormDialog } from './shared/FormDialog';
import { Input } from '@/components/ui/input';
import { Clock } from 'lucide-react';
import { format, differenceInMinutes } from 'date-fns';

interface TemplateCreationDialogProps {
  isOpen: boolean;
  startTime?: Date | null;
  endTime?: Date | null;
  onSave: (name: string, durationMinutes: number) => void;
  onCancel: () => void;
}

const TemplateCreationDialog: React.FC<TemplateCreationDialogProps> = ({
  isOpen,
  startTime,
  endTime,
  onSave,
  onCancel
}) => {
  const [templateName, setTemplateName] = useState('');
  const [customDuration, setCustomDuration] = useState<number | ''>('');

  // Reset form when dialog opens
  useEffect(() => {
    if (isOpen) {
      setTemplateName('');
      // If we have start and end times, calculate the default duration
      if (startTime && endTime) {
        const calculatedDuration = differenceInMinutes(endTime, startTime);
        setCustomDuration(calculatedDuration);
      } else {
        setCustomDuration(480); // Default 8 hours
      }
    }
  }, [isOpen, startTime, endTime]);

  // Calculate duration in minutes - use custom duration if no times provided
  const durationMinutes = startTime && endTime 
    ? differenceInMinutes(endTime, startTime) 
    : (typeof customDuration === 'number' ? customDuration : 0);

  // Format duration for display
  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) {
      return `${mins}min`;
    }
    if (mins === 0) {
      return `${hours}h`;
    }
    return `${hours}h ${mins}min`;
  };

  const handleSave = () => {
    if (templateName.trim() && durationMinutes > 0) {
      onSave(templateName.trim(), durationMinutes);
      setTemplateName('');
      setCustomDuration('');
    }
  };

  return (
    <FormDialog
      isOpen={isOpen}
      onClose={onCancel}
      title={
        <div className="flex items-center gap-2">
          <Clock className="h-5 w-5 text-blue-600" />
          Create Template
        </div>
      }
      description="Create a reusable shift template with custom duration and name"
      onSubmit={handleSave}
      submitText="Create Template"
      size="sm"
    >
      {/* Template Name Input */}
      <div className="space-y-2">
        <label htmlFor="template-name" className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Template Name
        </label>
        <Input
          id="template-name"
          value={templateName}
          onChange={(e) => setTemplateName(e.target.value)}
          placeholder="e.g., Morning Shift, Evening Shift"
          className="w-full"
          autoFocus
        />
      </div>

      {/* Duration Input */}
      <div className="space-y-2">
        <label htmlFor="template-duration" className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Duration (minutes)
        </label>
        <Input
          id="template-duration"
          type="number"
          value={customDuration}
          onChange={(e) => setCustomDuration(e.target.value === '' ? '' : parseInt(e.target.value, 10))}
          placeholder="480"
          min="15"
          max="1440"
          className="w-full"
        />
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Duration in minutes (e.g., 480 for 8 hours)
        </p>
      </div>

      {/* Duration Display */}
      <div className="bg-gray-50 dark:bg-gray-800 rounded-md p-3">
        <div className="flex items-center justify-between text-sm">
          <span className="font-medium text-gray-600 dark:text-gray-300">
            Duration:
          </span>
          <span className="font-semibold text-blue-600 dark:text-blue-400">
            {formatDuration(durationMinutes)}
          </span>
        </div>
        {startTime && endTime && (
          <div className="flex items-center justify-between text-sm mt-2">
            <span className="font-medium text-gray-600 dark:text-gray-300">
              Based on time range:
            </span>
            <span className="font-mono text-xs">
              {format(startTime, 'HH:mm')} - {format(endTime, 'HH:mm')}
            </span>
          </div>
        )}
      </div>
    </FormDialog>
  );
};

export default TemplateCreationDialog;